import { getItemUid } from './getItemUid';

import type {
    CatalogItem,
    GlobalCatalogItem,
    MappedCatalogItem,
} from '../model/types/CatalogItemSchema';
import type { CatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';

type SelectedItems = Record<string, Partial<MappedCatalogItem>>;

type ResolveCatalogRowSelectionResult = {
    entry: Partial<MappedCatalogItem> | undefined;
    mapKey: string;
    canonicalKey: string;
};

/**
 * Looks up `selectedItems` for a catalog row and returns both the **canonical** product id
 * (`getItemUid`) and the **actual object key** in the map (usually the same).
 *
 * After order hydration, global marketplace lines may temporarily exist under a key that matches
 * `entry.uuid` / `entry.uid` but not the list row’s `getItemUid` string; we resolve that here so
 * callers can use `mapKey` for Redux ops and `canonicalKey` everywhere else.
 */
export const resolveCatalogRowSelection = (
    selectedItems: SelectedItems,
    item: CatalogItem | GlobalCatalogItem,
    identity: CatalogIdentity,
): ResolveCatalogRowSelectionResult => {
    const canonicalKey = getItemUid(item, identity);
    const direct = selectedItems[canonicalKey];

    if (direct) {
        return { entry: direct, mapKey: canonicalKey, canonicalKey };
    }

    const mayAliasAfterOrderHydration =
        identity.flow === 'orders-add-items' && !('id' in item);

    if (mayAliasAfterOrderHydration) {
        const found = Object.entries(selectedItems).find(
            ([, entry]) =>
                entry.uuid === canonicalKey || entry.uid === canonicalKey,
        );
        if (found) {
            return { entry: found[1], mapKey: found[0], canonicalKey };
        }
    }

    return { entry: undefined, mapKey: canonicalKey, canonicalKey };
};
